import { FillHistoryInstanceType } from '@flipster/apis/v2/history/instance';
import { ProductType } from '@flipster/apis/v2/history/types';
import { TransOrderType } from '@flipster/apis/v2/order/trans';
import { FormattedMessage } from '@flipster/i18n';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { CardListItem as CardItem } from '@flipster/ui/CardList';

import { SymbolDisplay } from '../../../misc/display';
import { useRawSnapshotSpec } from '../../../shared/snapshot/use-snapshot-spec';
import { ExecFeeDisplay } from '../exec-fee-display';
import { HistoryOrderSideBadge } from '../order-side-badge';
import { card, horizontal, symbolRow } from '../styles';

interface Props {
  item: FillHistoryInstanceType;
  productType: ProductType;
}

export const FillMobileCard = ({ item, productType }: Props) => {
  const { instrumentsKeyBySymbol } = useRawSnapshotSpec();

  const instrument = instrumentsKeyBySymbol?.[item.symbol];

  return (
    <CardItem>
      <dl className={card}>
        <div className={symbolRow}>
          <SymbolDisplay
            symbol={item.symbol}
            icon={<SymbolDisplay.Icon size={24} />}
            mainLabel={<SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />}
          />
          <HistoryOrderSideBadge orderSide={item.side} productType={productType} />
        </div>
        <div className={horizontal}>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Fill_price' />
          </dt>
          <dd>
            <Formatted.Number
              value={item.lastPrice}
              precision={instrument?.pricePrecision}
              toFixed
              css={{ textStyle: 'Text/M/Monospace/Regular' }}
              suffix={instrument?.quoteCurrency}
            />
          </dd>
        </div>
        <div className={horizontal}>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Fill_qty' />
          </dt>
          <dd>
            <Formatted.Number
              value={item.lastQty}
              precision={instrument?.tradeQtyPrecision}
              toFixed
              css={{ textStyle: 'Text/M/Monospace/Regular' }}
            />
          </dd>
        </div>
        {productType === ProductType.Perpetual && (
          <div className={horizontal}>
            <dt>
              <FormattedMessage id='Autogen_common.r251126.s_Order_price' />
            </dt>
            <dd>
              {item.isMarket ? (
                <FormattedMessage id='Trade.r251008.Market' />
              ) : (
                <Formatted.Number
                  value={item.price}
                  precision={instrument?.pricePrecision}
                  toFixed
                  css={{ textStyle: 'Text/M/Monospace/Regular' }}
                  suffix={instrument?.quoteCurrency}
                />
              )}
            </dd>
          </div>
        )}
        <div className={horizontal}>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Fee_Type_slash_Fee' />
          </dt>
          <dd>
            <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2', textAlign: 'right' })}>
              {item.aggressorIndicator ? 'Taker' : 'Maker'}
            </div>
            <ExecFeeDisplay
              originalExecFee={item.execFee}
              aqxpBonusRedemption={item.execFeeAqxpRedeemed}
              voucherBonusRedemption={item.execFeeVoucherRedeemed}
              // https://prextech.slack.com/archives/C087D884VEE/p1763356773477699?thread_ts=1763345370.588609&cid=C087D884VEE
              // spot인 경우는 quoteCurrency로 보여준다. Perp는 settlementCurrency로 보여준다.
              suffix={instrument?.isSpot ? instrument.quoteCurrency : item.settlementCurrency}
            />
          </dd>
        </div>
        {productType === ProductType.Perpetual && (
          <div className={horizontal}>
            <dt>
              <FormattedMessage id='Autogen_common.r251126.s_Type' />
            </dt>
            <dd>{TransOrderType[item.orderType]}</dd>
          </div>
        )}
        <div className={horizontal}>
          <dt>
            <FormattedMessage id='Common.r250401.Date' />
          </dt>
          <dd>
            <Datetime value={item.matchTime} css={{ textStyle: 'Legacy/Number2' }} />
          </dd>
        </div>
      </dl>
    </CardItem>
  );
};
